import { useState, useEffect, useRef } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import Sidebar from "@/components/Sidebar";
import axios from "axios";
import { authAxios as ax } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Clock, BookOpen, FileText, Play, Square, Search, Phone, PhoneOff, PhoneIncoming, Mic, MicOff, Pause, ArrowRight, Send, Ear, Radio } from "lucide-react";
import { toast } from "sonner";
import { useKBSuggestions } from "@/hooks/useKBSuggestions";
import SuggestedArticlesPanel from "@/components/operator/SuggestedArticlesPanel";
import RecentCallsPanel from "@/components/operator/RecentCallsPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const langLabels = { en: "English", es: "Spanish", fr: "French", ht: "Haitian Creole" };

function ClockWidget({ t }) {
  const [active, setActive] = useState(null);
  const [project, setProject] = useState("general");
  const [elapsed, setElapsed] = useState("");
  const timer = useRef(null);

  const load = async () => {
    try {
      const res = await ax.get(`${API}/operator/active-clock`);
      if (res.data && res.data.entry_id) setActive(res.data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (active?.clock_in) {
      timer.current = setInterval(() => {
        const diff = Math.floor((Date.now() - new Date(active.clock_in).getTime()) / 1000);
        const h = Math.floor(diff / 3600);
        const m = Math.floor((diff % 3600) / 60);
        const s = diff % 60;
        setElapsed(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`);
      }, 1000);
    }
    return () => clearInterval(timer.current);
  }, [active]);

  const clockIn = async () => {
    try {
      const res = await ax.post(`${API}/operator/clock-in`, { project_id: project });
      setActive(res.data);
      toast.success(t("operator.clock_in"));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const clockOut = async () => {
    try {
      await ax.post(`${API}/operator/clock-out`);
      setActive(null);
      setElapsed("");
      toast.success(t("operator.clock_out"));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="border border-[#E5E7EB] bg-white p-6" data-testid="clock-widget">
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-10 h-10 flex items-center justify-center ${active ? "bg-[#00C853]" : "bg-[#E5E7EB]"}`}>
          <Clock size={18} className={active ? "text-white" : "text-[#9CA3AF]"} />
        </div>
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#9CA3AF]">
            {active ? t("operator.clocked_in") : t("operator.not_clocked")}
          </p>
          {elapsed && <p className="text-3xl font-black text-[#0A0A0A] tracking-tight font-mono">{elapsed}</p>}
        </div>
      </div>
      {!active ? (
        <div className="space-y-3">
          <Input className="rounded-none" placeholder={t("operator.project_code")} value={project} onChange={(e) => setProject(e.target.value)} data-testid="clock-project-input" />
          <Button className="rounded-none bg-[#00C853] hover:bg-[#00C853]/90 text-white w-full h-10 text-xs font-bold tracking-[0.1em] uppercase" onClick={clockIn} data-testid="clock-in-btn">
            <Play size={14} className="mr-1" /> {t("operator.clock_in")}
          </Button>
        </div>
      ) : (
        <Button className="rounded-none bg-[#FF2A2A] hover:bg-[#FF2A2A]/90 text-white w-full h-10 text-xs font-bold tracking-[0.1em] uppercase" onClick={clockOut} data-testid="clock-out-btn">
          <Square size={14} className="mr-1" /> {t("operator.clock_out")}
        </Button>
      )}
    </div>
  );
}

function KnowledgeBase({ t }) {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const searchKB = async (q, cat) => {
    try {
      const params = {};
      if (q) params.q = q;
      if (cat) params.category = cat;
      const res = await ax.get(`${API}/operator/knowledge-base`, { params });
      setArticles(res.data);
    } catch {}
  };

  useEffect(() => {
    searchKB("", "");
    ax.get(`${API}/operator/knowledge-base/categories`).then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const catColors = {
    "Access Control": "bg-[#002FA7]/10 text-[#002FA7] border-[#002FA7]/20",
    "Radar Systems": "bg-[#00C853]/10 text-[#00C853] border-[#00C853]/20",
    "Video Solutions": "bg-[#FFB800]/10 text-[#FFB800] border-[#FFB800]/20",
    "Drone Security": "bg-[#FF2A2A]/10 text-[#FF2A2A] border-[#FF2A2A]/20",
    "Protocols": "bg-[#0A0A0A]/10 text-[#0A0A0A] border-[#0A0A0A]/20",
  };

  return (
    <div className="border border-[#E5E7EB] bg-white p-6" data-testid="knowledge-base">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 flex items-center justify-center bg-[#002FA7]">
          <BookOpen size={18} className="text-white" />
        </div>
        <div>
          <h3 className="text-lg font-semibold">{t("operator.kb_title")}</h3>
          <p className="text-[10px] tracking-[0.15em] uppercase text-[#9CA3AF] font-bold">{articles.length} articles</p>
        </div>
      </div>
      <div className="relative mb-3">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
        <Input
          className="rounded-none pl-9"
          placeholder={t("operator.kb_search")}
          value={query}
          onChange={(e) => { setQuery(e.target.value); searchKB(e.target.value, activeCategory); }}
          data-testid="kb-search-input"
        />
      </div>
      {/* Category filters */}
      <div className="flex flex-wrap gap-1.5 mb-3" data-testid="kb-categories">
        <button
          className={`px-2 py-1 text-[10px] font-bold uppercase tracking-[0.1em] border transition-colors ${!activeCategory ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#4B5563] border-[#E5E7EB] hover:border-[#0A0A0A]"}`}
          onClick={() => { setActiveCategory(""); searchKB(query, ""); }}
          data-testid="kb-cat-all"
        >All</button>
        {categories.map((cat) => (
          <button
            key={cat}
            className={`px-2 py-1 text-[10px] font-bold uppercase tracking-[0.1em] border transition-colors ${activeCategory === cat ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : `${catColors[cat]?.split(" ")[0] || "bg-white"} ${catColors[cat]?.split(" ")[1] || "text-[#4B5563]"} border-[#E5E7EB] hover:border-[#0A0A0A]`}`}
            onClick={() => { setActiveCategory(cat); searchKB(query, cat); }}
            data-testid={`kb-cat-${cat.toLowerCase().replace(/\s/g, "-")}`}
          >{cat}</button>
        ))}
      </div>
      <div className="space-y-2 max-h-[420px] overflow-y-auto thin-scrollbar">
        {articles.map((a) => (
          <div
            key={a.article_id}
            className={`p-3.5 border transition-all duration-200 cursor-pointer ${expandedId === a.article_id ? "bg-white border-[#002FA7]/30 shadow-sm" : "bg-[#F8F9FA] border-[#E5E7EB] hover:border-[#002FA7]/20"}`}
            onClick={() => setExpandedId(expandedId === a.article_id ? null : a.article_id)}
            data-testid={`kb-article-${a.article_id}`}
          >
            <div className="flex justify-between items-start mb-1">
              <h4 className="text-sm font-semibold text-[#0A0A0A] pr-2">{a.title}</h4>
              <Badge variant="outline" className={`rounded-none text-[9px] font-bold shrink-0 ${catColors[a.category] || ""}`}>{a.category}</Badge>
            </div>
            <p className={`text-xs text-[#4B5563] leading-relaxed ${expandedId === a.article_id ? "" : "line-clamp-2"}`}>{a.content}</p>
            <div className="flex gap-1 mt-2 flex-wrap">
              {a.tags?.map((tag) => (
                <span key={tag} className="text-[9px] bg-[#002FA7]/5 text-[#002FA7] px-1.5 py-0.5 font-bold">{tag}</span>
              ))}
            </div>
            {(a.pdf_file_id || a.video_url) && (
              <div className="flex gap-2 mt-2 pt-2 border-t border-[#E5E7EB]" onClick={(e) => e.stopPropagation()}>
                {a.pdf_file_id && (
                  <a
                    href={`${API}/kb/file/${a.pdf_file_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-[10px] text-[#002FA7] font-bold uppercase tracking-[0.1em] hover:underline"
                    data-testid={`kb-article-pdf-${a.article_id}`}
                  >
                    PDF: {a.pdf_filename || "Document"}
                  </a>
                )}
                {a.video_url && (
                  <a
                    href={a.video_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-[10px] text-[#FF2A2A] font-bold uppercase tracking-[0.1em] hover:underline"
                    data-testid={`kb-article-video-${a.article_id}`}
                  >
                    {(a.video_type || "Video").toUpperCase()}
                  </a>
                )}
              </div>
            )}
            {expandedId === a.article_id && a.video_type === "youtube" && a.video_id && (
              <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                <iframe
                  width="100%"
                  height="200"
                  src={`https://www.youtube.com/embed/${a.video_id}`}
                  title={a.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  data-testid={`kb-yt-embed-${a.article_id}`}
                />
              </div>
            )}
            {expandedId === a.article_id && a.video_type === "vimeo" && a.video_id && (
              <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                <iframe
                  width="100%"
                  height="200"
                  src={`https://player.vimeo.com/video/${a.video_id}`}
                  title={a.title}
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  data-testid={`kb-vimeo-embed-${a.article_id}`}
                />
              </div>
            )}
          </div>
        ))}
        {articles.length === 0 && <p className="text-sm text-[#9CA3AF] text-center py-4">{t("common.no_data")}</p>}
      </div>
    </div>
  );
}

function Softphone({ t, userRole, uiLanguage }) {
  const [status, setStatus] = useState("initializing"); // initializing | ready | connecting | on_call | ringing_in | error
  const [number, setNumber] = useState("");
  const [callTime, setCallTime] = useState(0);
  const [muted, setMuted] = useState(false);
  const [supervisionMode, setSupervisionMode] = useState(null);
  const [error, setError] = useState("");
  const [deviceReady, setDeviceReady] = useState(false);
  const [incomingCall, setIncomingCall] = useState(null); // Twilio Call object for incoming
  const [incomingInfo, setIncomingInfo] = useState(null); // { from, to }
  const [liveTranslation, setLiveTranslation] = useState(null); // { original, translated, lang }
  const [translating, setTranslating] = useState(false);
  const [liveTargetLang, setLiveTargetLang] = useState(uiLanguage && uiLanguage !== "en" ? uiLanguage : "es");
  // Keep the live target sync'd to the operator's UI language whenever they switch globally
  useEffect(() => {
    if (uiLanguage && uiLanguage !== "en") setLiveTargetLang(uiLanguage);
  }, [uiLanguage]);
  const [callNotes, setCallNotes] = useState("");
  const deviceRef = useRef(null);
  const connectionRef = useRef(null);
  const timer = useRef(null);
  const translationTimer = useRef(null);
  const callSidRef = useRef(null);
  const liveTranslationRef = useRef(null);
  const callNotesRef = useRef("");
  const isAdmin = userRole === "super-admin" || userRole === "admin";
  const logger = (msg) => {
    if (process.env.NODE_ENV !== "production") {
      try { console.log(`[T2S Softphone] ${msg}`); } catch {}
    }
  };

  // Keep refs in sync so the disconnect handler (closure) can read latest values
  useEffect(() => { liveTranslationRef.current = liveTranslation; }, [liveTranslation]);
  useEffect(() => { callNotesRef.current = callNotes; }, [callNotes]);

  // Persist transcript + notes to backend (best-effort, never throws to UI)
  const persistCallContext = async (callSid) => {
    if (!callSid) return;
    try {
      const messages = liveTranslationRef.current?.messages || [];
      if (messages.length > 0) {
        await ax.post(`${API}/calls/${callSid}/transcript`, { messages });
      }
      // Always fetch active clock to find the active project binding
      let projectId = null;
      try {
        const clk = await ax.get(`${API}/operator/active-clock`);
        projectId = clk.data?.project_id || null;
      } catch {}
      await ax.put(`${API}/calls/${callSid}/notes`, {
        notes: callNotesRef.current || "",
        project_id: projectId,
      });
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.warn("[T2S] Failed to persist call context:", e?.message);
    }
  };

  // AI-Assist Copilot — listens to the live transcript stream and surfaces
  // matching KB articles in real time.
  const copilotActive = status === "on_call" && translating;
  const { suggestions: copilotSuggestions, loading: copilotLoading } = useKBSuggestions(
    liveTranslation?.messages,
    copilotActive,
    1200
  );

  // Initialize Twilio Device on mount
  useEffect(() => {
    let cancelled = false;
    const initDevice = async () => {
      try {
        const res = await ax.post(`${API}/twilio/token`);
        const token = res.data?.token;
        if (!token || cancelled) {
          if (!cancelled) { setError("No voice token received. Twilio may not be configured."); setStatus("error"); }
          return;
        }

        const { Device } = await import("@twilio/voice-sdk");

        const device = new Device(token, {
          codecPreferences: ["opus", "pcmu"],
          logLevel: "warn",
          disableAudioContextSounds: false,
        });

        device.on("registered", () => {
          if (!cancelled) { setDeviceReady(true); setStatus("ready"); setError(""); }
        });
        device.on("error", (err) => {
          const msg = err?.message || "";
          if (msg.includes("Devices not found") || msg.includes("NotFoundError")) {
            logger("Audio output device not found, using default. Non-critical.");
            if (!cancelled && !deviceReady) { setDeviceReady(true); setStatus("ready"); }
            return;
          }
          if (!cancelled) { setError(msg || "Device error"); setStatus("error"); }
        });
        device.on("tokenWillExpire", async () => {
          try {
            const r = await ax.post(`${API}/twilio/token`);
            if (r.data?.token) device.updateToken(r.data.token);
          } catch {}
        });

        // INCOMING CALL HANDLER
        device.on("incoming", (call) => {
          logger(`Incoming call from: ${call.parameters?.From || "Unknown"}`);
          setIncomingCall(call);
          setIncomingInfo({
            from: call.parameters?.From || "Unknown",
            to: call.parameters?.To || "",
            callSid: call.parameters?.CallSid || "",
          });
          setStatus("ringing_in");

          call.on("cancel", () => {
            logger("Incoming call cancelled by caller");
            setIncomingCall(null);
            setIncomingInfo(null);
            setStatus("ready");
          });
          call.on("disconnect", () => {
            setStatus("ready");
            connectionRef.current = null;
            setIncomingCall(null);
            setIncomingInfo(null);
            setMuted(false);
            setLiveTranslation(null);
            clearInterval(translationTimer.current);
          });
          call.on("reject", () => {
            setIncomingCall(null);
            setIncomingInfo(null);
            setStatus("ready");
          });
          call.on("error", (err) => {
            setError(err?.message || "Call error");
            setStatus("ready");
            setIncomingCall(null);
            setIncomingInfo(null);
          });
        });

        await device.register();
        deviceRef.current = device;
      } catch (err) {
        if (!cancelled) {
          const msg = err?.response?.data?.detail || err?.message || "Failed to initialize phone";
          if (msg.includes("NotAllowedError") || msg.includes("Permission")) {
            setError("Microphone access denied. Please allow microphone permission and reload.");
          } else if (msg.includes("NotFoundError") || msg.includes("Devices not found")) {
            setError("No microphone found. Please connect a microphone and reload.");
          } else if (msg.includes("API Key") || msg.includes("not configured")) {
            setError("Telephony service is being configured. Please try again shortly.");
          } else {
            setError(msg);
          }
          setStatus("error");
        }
      }
    };
    initDevice();
    return () => { cancelled = true; clearInterval(translationTimer.current); deviceRef.current?.destroy(); };
  }, []);

  // Call timer
  useEffect(() => {
    if (status === "on_call") {
      timer.current = setInterval(() => setCallTime((p) => p + 1), 1000);
    } else {
      clearInterval(timer.current);
      if (status !== "connecting") setCallTime(0);
    }
    return () => clearInterval(timer.current);
  }, [status]);

  const dial = (n) => setNumber((p) => p + n);
  const clearNumber = () => setNumber("");
  const formatTime = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const startCall = async () => {
    if (!number) { setError(t("softphone.error_no_number")); return; }
    if (!deviceRef.current) { setError(t("softphone.error_not_ready")); return; }
    setError("");
    setStatus("connecting");

    try {
      // Format to E.164 if not already
      let dialNumber = number.replace(/[\s\-()]/g, "");
      if (!dialNumber.startsWith("+")) dialNumber = "+1" + dialNumber;

      const call = await deviceRef.current.connect({
        params: { To: dialNumber },
      });
      connectionRef.current = call;

      call.on("accept", () => {
        callSidRef.current = call?.parameters?.CallSid || null;
        setStatus("on_call");
        startLiveTranslation();
      });
      call.on("disconnect", () => {
        const sid = callSidRef.current;
        setStatus("ready");
        connectionRef.current = null;
        setMuted(false);
        setLiveTranslation(null);
        clearInterval(translationTimer.current);
        // Fire-and-forget transcript & notes persistence
        if (sid) persistCallContext(sid);
        callSidRef.current = null;
      });
      call.on("cancel", () => { setStatus("ready"); connectionRef.current = null; });
      call.on("reject", () => { setStatus("ready"); setError("Call was rejected"); connectionRef.current = null; });
      call.on("error", (err) => { setError(err?.message || "Call failed"); setStatus("ready"); connectionRef.current = null; });
    } catch (err) {
      setError(err?.message || "Failed to connect call");
      setStatus("ready");
    }
  };

  const endCall = () => {
    const sid = callSidRef.current;
    connectionRef.current?.disconnect();
    connectionRef.current = null;
    setStatus("ready");
    setMuted(false);
    setSupervisionMode(null);
    setLiveTranslation(null);
    setTranslating(false);
    clearInterval(translationTimer.current);
    if (sid) persistCallContext(sid);
    callSidRef.current = null;
    setCallNotes("");
  };

  const toggleMute = () => {
    if (connectionRef.current) {
      const newMuted = !muted;
      connectionRef.current.mute(newMuted);
      setMuted(newMuted);
    }
  };

  // Accept incoming call + start live translation
  const acceptIncoming = () => {
    if (!incomingCall) return;
    logger("Accepting incoming call");
    incomingCall.accept();
    connectionRef.current = incomingCall;
    callSidRef.current = incomingCall?.parameters?.CallSid || null;
    // Wire disconnect persistence for incoming calls too
    incomingCall.on("disconnect", () => {
      const sid = callSidRef.current;
      setStatus("ready");
      connectionRef.current = null;
      setMuted(false);
      setLiveTranslation(null);
      clearInterval(translationTimer.current);
      if (sid) persistCallContext(sid);
      callSidRef.current = null;
    });
    setStatus("on_call");
    setIncomingCall(null);
    toast.success(t("softphone.call_accepted"));

    // Start Gemini auto-translation polling
    startLiveTranslation();
  };

  // Reject incoming call
  const rejectIncoming = () => {
    if (!incomingCall) return;
    logger("Rejecting incoming call");
    incomingCall.reject();
    setIncomingCall(null);
    setIncomingInfo(null);
    setStatus("ready");
    toast.info(t("softphone.call_rejected"));
  };

  // Real-time auto-translation via Gemini LLM
  const startLiveTranslation = () => {
    clearInterval(translationTimer.current);
    setLiveTranslation({ status: "active", messages: [] });
    setTranslating(true);
    logger("Live translation engine started");
  };

  const translateMessage = async (text, fromLang, toLang) => {
    if (!text.trim()) return;
    try {
      const res = await ax.post(`${API}/ai/translate`, {
        text,
        source_lang: fromLang,
        target_lang: toLang,
      });
      const translated = res.data?.translated || "";
      setLiveTranslation((prev) => ({
        ...prev,
        messages: [
          ...((prev?.messages || []).slice(-8)),
          { original: text, translated, from: fromLang, to: toLang, time: new Date().toLocaleTimeString() },
        ],
      }));
      return translated;
    } catch {
      return null;
    }
  };

  // Stop translation when call ends
  useEffect(() => {
    if (status !== "on_call") {
      clearInterval(translationTimer.current);
      if (translating) setTranslating(false);
    }
  }, [status, translating]);

  const statusLabel = {
    initializing: t("softphone.initializing"),
    ready: t("softphone.ready"),
    connecting: t("softphone.connecting"),
    ringing_in: t("softphone.incoming"),
    on_call: `${t("softphone.on_call")} - ${formatTime(callTime)}`,
    error: t("softphone.error_label"),
  };

  const statusColor = {
    initializing: "bg-[#FFB800]",
    ready: "bg-[#0A0A0A]",
    connecting: "bg-[#FFB800] animate-pulse",
    ringing_in: "bg-[#002FA7] animate-pulse",
    on_call: "bg-[#00C853]",
    error: "bg-[#FF2A2A]",
  };

  return (
    <div className="border border-[#E5E7EB] bg-white p-6" data-testid="softphone">
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-10 h-10 flex items-center justify-center ${statusColor[status] || "bg-[#0A0A0A]"}`}>
          <Phone size={18} className="text-white" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold">{t("softphone.title")}</h3>
          <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#9CA3AF]">
            {statusLabel[status] || status}
          </p>
        </div>
        {deviceReady && <span className="w-2.5 h-2.5 rounded-full bg-[#00C853]" title="Device registered" />}
      </div>

      {/* Error banner */}
      {error && (
        <div className="mb-3 p-2.5 bg-[#FF2A2A]/5 border border-[#FF2A2A]/20 flex items-start gap-2" data-testid="softphone-error">
          <PhoneOff size={14} className="text-[#FF2A2A] shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-xs text-[#FF2A2A] font-medium">{error}</p>
            {status === "error" && (
              <button className="text-[10px] text-[#002FA7] font-bold mt-1 underline" onClick={() => window.location.reload()}>
                {t("softphone.retry")}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Incoming Call Ringer Overlay */}
      {status === "ringing_in" && incomingInfo && (
        <div className="mb-4 p-4 border-2 border-[#002FA7] bg-[#002FA7]/5 animate-fade-in" data-testid="incoming-call-overlay">
          <div className="flex items-center gap-3 mb-3">
            <div className="relative">
              <div className="w-12 h-12 bg-[#002FA7] flex items-center justify-center">
                <PhoneIncoming size={22} className="text-white" />
              </div>
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-[#00C853] rounded-full animate-ping" />
            </div>
            <div className="flex-1">
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#002FA7]">{t("softphone.incoming")}</p>
              <p className="text-lg font-black text-[#0A0A0A] tracking-tight font-mono">{incomingInfo.from}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              className="rounded-none flex-1 bg-[#00C853] hover:bg-[#00C853]/90 text-white h-11 text-xs font-bold tracking-[0.1em] uppercase"
              onClick={acceptIncoming}
              data-testid="incoming-accept-btn"
            >
              <Phone size={16} className="mr-1.5" /> {t("softphone.answer")}
            </Button>
            <Button
              className="rounded-none flex-1 bg-[#FF2A2A] hover:bg-[#FF2A2A]/90 text-white h-11 text-xs font-bold tracking-[0.1em] uppercase"
              onClick={rejectIncoming}
              data-testid="incoming-reject-btn"
            >
              <PhoneOff size={16} className="mr-1.5" /> {t("softphone.reject")}
            </Button>
          </div>
        </div>
      )}

      {/* Number display */}
      <div className="relative mb-3">
        <Input className="rounded-none text-center text-lg font-mono tracking-wider pr-8" value={number} readOnly placeholder="+1 (XXX) XXX-XXXX" data-testid="softphone-number" />
        {number && status !== "on_call" && status !== "connecting" && (
          <button className="absolute right-2 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#FF2A2A] text-xs font-bold" onClick={clearNumber}>X</button>
        )}
      </div>

      {/* Dial pad */}
      {status !== "on_call" && status !== "connecting" && (
        <div className="grid grid-cols-3 gap-2 mb-4">
          {["1","2","3","4","5","6","7","8","9","*","0","#"].map((n) => (
            <button key={n} className="dial-btn h-10 bg-[#F8F9FA] border border-[#E5E7EB] font-bold text-[#0A0A0A] hover:bg-[#E5E7EB] text-sm" onClick={() => dial(n)} data-testid={`dial-${n}`}>{n}</button>
          ))}
        </div>
      )}

      {/* Call controls */}
      <div className="flex gap-2">
        {status === "ready" || status === "error" ? (
          <Button className="rounded-none flex-1 bg-[#00C853] hover:bg-[#00C853]/90 text-white h-10 text-xs font-bold tracking-[0.1em] uppercase" onClick={startCall} disabled={!deviceReady || !number} data-testid="softphone-call-btn">
            <Phone size={14} className="mr-1" /> {t("softphone.dial")}
          </Button>
        ) : status === "connecting" ? (
          <Button className="rounded-none flex-1 bg-[#FFB800] text-[#0A0A0A] h-10 text-xs font-bold tracking-[0.1em] uppercase animate-pulse" disabled data-testid="softphone-connecting">
            {t("softphone.connecting")}...
          </Button>
        ) : (
          <>
            <Button className="rounded-none flex-1 bg-[#FF2A2A] hover:bg-[#FF2A2A]/90 text-white h-10 text-xs font-bold tracking-[0.1em] uppercase" onClick={endCall} data-testid="softphone-hangup-btn">
              <PhoneOff size={14} className="mr-1" /> {t("softphone.hang_up")}
            </Button>
            <Button variant="outline" className={`rounded-none h-10 ${muted ? "bg-[#FF2A2A]/10 border-[#FF2A2A]/30" : ""}`} onClick={toggleMute} data-testid="softphone-mute-btn">
              {muted ? <MicOff size={14} className="text-[#FF2A2A]" /> : <Mic size={14} />}
            </Button>
          </>
        )}
      </div>

      {/* Supervision Controls (Admin/Super-Admin only) */}
      {isAdmin && status === "on_call" && (
        <div className="mt-4 border-t border-[#E5E7EB] pt-4" data-testid="supervision-controls">
          <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#9CA3AF] mb-2">
            {t("supervision.monitoring")}
          </p>
          {!supervisionMode ? (
            <div className="flex gap-2">
              <Button
                className="rounded-none flex-1 bg-[#FFB800] hover:bg-[#FFB800]/90 text-[#0A0A0A] h-9 text-xs font-bold tracking-[0.1em] uppercase"
                onClick={() => { setSupervisionMode("whisper"); toast.success(t("supervision.whisper_active")); }}
                data-testid="whisper-btn"
              >
                <Ear size={14} className="mr-1" /> {t("supervision.whisper")}
              </Button>
              <Button
                className="rounded-none flex-1 bg-[#FF2A2A] hover:bg-[#FF2A2A]/90 text-white h-9 text-xs font-bold tracking-[0.1em] uppercase"
                onClick={() => { setSupervisionMode("barge"); toast.success(t("supervision.barge_active")); }}
                data-testid="barge-btn"
              >
                <Radio size={14} className="mr-1" /> {t("supervision.barge")}
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <div className={`flex items-center gap-2 p-2.5 border ${supervisionMode === "whisper" ? "border-[#FFB800] bg-[#FFB800]/5" : "border-[#FF2A2A] bg-[#FF2A2A]/5"}`}>
                {supervisionMode === "whisper" ? <Ear size={14} className="text-[#FFB800]" /> : <Radio size={14} className="text-[#FF2A2A]" />}
                <div className="flex-1">
                  <p className="text-xs font-bold text-[#0A0A0A]">{supervisionMode === "whisper" ? t("supervision.whisper") : t("supervision.barge")}</p>
                  <p className="text-[10px] text-[#9CA3AF]">{supervisionMode === "whisper" ? t("supervision.whisper_desc") : t("supervision.barge_desc")}</p>
                </div>
                <div className="w-2 h-2 rounded-full bg-[#00C853] animate-pulse" />
              </div>
              <Button
                variant="outline"
                className="rounded-none w-full h-8 text-[10px] font-bold tracking-[0.1em] uppercase"
                onClick={() => { setSupervisionMode(null); toast.info(t("supervision.end_supervision")); }}
                data-testid="end-supervision-btn"
              >
                {t("supervision.end_supervision")}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Live Auto-Translation Panel (Gemini LLM) */}
      {status === "on_call" && translating && (
        <div className="mt-4 border-t border-[#E5E7EB] pt-4" data-testid="live-translation-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[#002FA7] animate-pulse" />
              <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#002FA7]">{t("softphone.live_translation")}</p>
            </div>
            <span className="text-[9px] text-[#9CA3AF] font-mono">Gemini 3 Flash</span>
          </div>

          {/* Quick translate input — defaults target to the operator's current UI language */}
          <div className="flex gap-1.5 mb-3">
            <Input
              className="rounded-none h-8 text-xs flex-1"
              placeholder={t("softphone.type_to_translate")}
              data-testid="live-translate-input"
              onKeyDown={async (e) => {
                if (e.key === "Enter" && e.target.value.trim()) {
                  const text = e.target.value.trim();
                  e.target.value = "";
                  // Use the operator's active workspace language as the translation target,
                  // falling back to ES (the default outbound). This keeps the live-translation
                  // feed structurally aligned with the operator's display preference.
                  const target = uiLanguage && uiLanguage !== "en" ? uiLanguage : "es";
                  await translateMessage(text, "en", target);
                }
              }}
            />
            <Select value={liveTargetLang} onValueChange={setLiveTargetLang} data-testid="live-translate-target">
              <SelectTrigger className="rounded-none h-8 w-16 text-[10px] font-bold">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="en">EN</SelectItem>
                <SelectItem value="es">ES</SelectItem>
                <SelectItem value="fr">FR</SelectItem>
                <SelectItem value="ht">HT</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Translation messages feed */}
          <div className="space-y-1.5 max-h-32 overflow-y-auto thin-scrollbar">
            {(liveTranslation?.messages || []).map((msg, i) => (
              <div key={i} className="p-2 bg-[#F8F9FA] border border-[#E5E7EB] text-xs" data-testid={`translation-msg-${i}`}>
                <div className="flex items-center gap-1 mb-0.5">
                  <span className="text-[8px] font-bold uppercase px-1 bg-[#002FA7]/10 text-[#002FA7]">{msg.from} &rarr; {msg.to}</span>
                  <span className="text-[8px] text-[#9CA3AF] font-mono ml-auto">{msg.time}</span>
                </div>
                <p className="text-[#9CA3AF] line-through">{msg.original}</p>
                <p className="text-[#0A0A0A] font-medium">{msg.translated}</p>
              </div>
            ))}
            {(liveTranslation?.messages || []).length === 0 && (
              <p className="text-[10px] text-[#9CA3AF] text-center py-2">{t("softphone.translation_ready")}</p>
            )}
          </div>

          {/* AI-Assist Copilot — real-time KB suggestions */}
          <SuggestedArticlesPanel
            suggestions={copilotSuggestions}
            loading={copilotLoading}
            active={copilotActive}
          />

          {/* Operator call notes — captured during the call, persisted on disconnect */}
          {status === "on_call" && (
            <div className="mt-4 border-t border-[#E5E7EB] pt-4" data-testid="call-notes-block">
              <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#9CA3AF] mb-1.5">
                Call Notes
              </p>
              <Textarea
                className="rounded-none h-20 text-xs"
                placeholder="Issue summary, resolution steps, escalation flags..."
                value={callNotes}
                onChange={(e) => setCallNotes(e.target.value)}
                data-testid="call-notes-input"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function LanguageProfile({ t, user }) {
  const [proficiency, setProficiency] = useState(user?.language_proficiency || {});
  const [saving, setSaving] = useState(false);

  const updateProf = async () => {
    setSaving(true);
    try {
      await ax.put(`${API}/operator/profile`, { language_proficiency: proficiency, languages: Object.keys(proficiency).filter((k) => proficiency[k] > 0) });
      toast.success("Profile updated");
    } catch { toast.error("Failed"); }
    setSaving(false);
  };

  return (
    <div className="border border-[#E5E7EB] bg-white p-6" data-testid="language-profile">
      <h3 className="text-lg font-semibold mb-4">{t("operator.lang_profile")}</h3>
      <div className="space-y-4">
        {["en", "es", "fr", "ht"].map((lang) => (
          <div key={lang}>
            <div className="flex justify-between text-sm mb-1">
              <span className="font-medium">{langLabels[lang]}</span>
              <span className="text-[#002FA7] font-bold">{proficiency[lang] || 0}/5</span>
            </div>
            <Slider
              value={[proficiency[lang] || 0]}
              onValueChange={([v]) => setProficiency({ ...proficiency, [lang]: v })}
              max={5} step={1}
              className="[&_[role=slider]]:rounded-none [&_[role=slider]]:bg-[#002FA7] [&_[role=slider]]:border-0"
              data-testid={`proficiency-${lang}`}
            />
          </div>
        ))}
        <Button className="rounded-none bg-[#002FA7] hover:bg-[#00227A] text-white w-full h-9 text-xs font-bold tracking-[0.1em] uppercase" onClick={updateProf} disabled={saving} data-testid="save-proficiency-btn">
          {t("common.save")}
        </Button>
      </div>
    </div>
  );
}

function TranslationChat({ t }) {
  const [text, setText] = useState("");
  const [from, setFrom] = useState("en");
  const [to, setTo] = useState("es");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const translate = async () => {
    if (!text) return;
    setLoading(true);
    try {
      const res = await ax.post(`${API}/ai/translate`, { text, source_lang: from, target_lang: to });
      setResult(res.data);
    } catch { toast.error("Translation failed"); }
    setLoading(false);
  };

  return (
    <div className="border border-[#E5E7EB] bg-white p-6" data-testid="translation-chat">
      <h3 className="text-lg font-semibold mb-4">{t("ai.translate_title")}</h3>
      <div className="flex gap-2 mb-3">
        <Select value={from} onValueChange={setFrom}>
          <SelectTrigger className="rounded-none flex-1" data-testid="translate-from"><SelectValue /></SelectTrigger>
          <SelectContent className="rounded-none">
            {Object.entries(langLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <ArrowRight size={18} className="self-center text-[#9CA3AF] shrink-0" />
        <Select value={to} onValueChange={setTo}>
          <SelectTrigger className="rounded-none flex-1" data-testid="translate-to"><SelectValue /></SelectTrigger>
          <SelectContent className="rounded-none">
            {Object.entries(langLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <Textarea className="rounded-none mb-3 h-20" placeholder={t("ai.enter_text")} value={text} onChange={(e) => setText(e.target.value)} data-testid="translate-input" />
      <Button className="rounded-none bg-[#002FA7] hover:bg-[#00227A] text-white w-full h-9 text-xs font-bold tracking-[0.1em] uppercase mb-3" onClick={translate} disabled={loading} data-testid="translate-btn">
        {loading ? t("common.loading") : t("ai.translate")}
      </Button>
      {result && (
        <div className="bg-[#F8F9FA] border border-[#E5E7EB] p-4" data-testid="translate-result">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#9CA3AF] mb-1">{langLabels[to]}</p>
          <p className="text-sm text-[#0A0A0A]">{result.translated}</p>
        </div>
      )}
    </div>
  );
}

export default function OperatorWorkspace() {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [scripts, setScripts] = useState([]);

  useEffect(() => {
    ax.get(`${API}/operator/scripts`).then((r) => setScripts(r.data)).catch(() => {});
  }, []);

  return (
    <div className="flex h-screen bg-[#F8F9FA]">
      <Sidebar />
      <main className="flex-1 overflow-auto">
        <div className="p-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#0A0A0A] tracking-tight mb-6" data-testid="operator-title">
            {t("operator.title")}
          </h2>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left column: Clock + Language Profile */}
            <div className="space-y-6">
              <ClockWidget t={t} />
              <LanguageProfile t={t} user={user} />
            </div>

            {/* Middle: Knowledge Base + Scripts */}
            <div className="space-y-6">
              <KnowledgeBase t={t} />
              <div className="border border-[#E5E7EB] bg-white p-6" data-testid="scripts-panel">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#FFB800]">
                    <FileText size={18} className="text-white" />
                  </div>
                  <h3 className="text-lg font-semibold">{t("operator.scripts_title")}</h3>
                </div>
                <div className="space-y-2 max-h-48 overflow-y-auto thin-scrollbar">
                  {scripts.map((s) => (
                    <div key={s.script_id} className="p-3 bg-[#F8F9FA] border border-[#E5E7EB]" data-testid={`script-item-${s.script_id}`}>
                      <p className="text-sm font-semibold text-[#0A0A0A]">{s.title}</p>
                      <p className="text-xs text-[#4B5563] mt-1">{s.content?.en?.substring(0, 80)}...</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Softphone + Translation + Recent Calls */}
            <div className="space-y-6">
              <Softphone t={t} userRole={user?.role} uiLanguage={language} />
              <RecentCallsPanel t={t} />
              <TranslationChat t={t} />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
